import { useEffect, useState } from "react";
import { TCourse } from "../@types";
import { apis, callApi } from "../api";
import { AccountService } from "./useAccount";

export type TMyServicesService = {
	listeners: (() => void)[];
	loading: boolean;
	courses: TCourse[];

	addEventListener: (cb: () => void) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;

	setLoading: (loading: boolean) => void;
	setCourses: (courses: TCourse[]) => void;
	addCourse: (course: TCourse) => void;
};

export const MyServicesService: TMyServicesService = {
	listeners: [],
	loading: false,
	courses: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	clearData() {
		this.loading = false;
		this.courses = [];
		this.emitChanges();
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setCourses(courses) {
		this.courses = courses;
	},

	addCourse(course) {
		this.courses = [...this.courses, course];
	},
};

export function useMyServicesInit() {
	useEffect(() => {
		const getCourses = async () => {
			try {
				MyServicesService.setLoading(true);
				MyServicesService.emitChanges();
				const response = await callApi({
					method: "GET",
					url: apis.MY_COURSES,
				});

				if (response?.courses) {
					MyServicesService.setCourses(response.courses);
				}
			} catch (error) {
			} finally {
				MyServicesService.setLoading(false);
				MyServicesService.emitChanges();
			}
		};

		getCourses();
	}, []);
}

export function useMyServices() {
	const [loading, setLoading] = useState(MyServicesService.loading);
	const [courses, setCourses] = useState(MyServicesService.courses);

	useEffect(() => {
		const updateData = () => {
			setLoading(MyServicesService.loading);
			setCourses(MyServicesService.courses);
		};

		MyServicesService.addEventListener(updateData);

		return () => {
			MyServicesService.removeEventListener(updateData);
		};
	}, []);

	const createCourse = async (): Promise<TCourse | undefined> => {
		try {
			if (!AccountService.savedAccount) {
				return;
			}
			const body = {
				name: `${AccountService.savedAccount.firstName}'s Service`,
			};

			const response = await callApi({
				method: "POST",
				url: apis.COURSES,
				body,
			});

			if (!response?.course) {
				return;
			}

			const course: TCourse = {
				...response.course,
				courseDocuments: [],
				lesson: [],
			};
			MyServicesService.addCourse(course);
			MyServicesService.emitChanges();
			return course;
		} catch (error) {}
	};

	const cloneCourse = async (id: number): Promise<TCourse | undefined> => {
		try {
			if (!AccountService.savedAccount) {
				return;
			}
			const body = {
				name: `${AccountService.savedAccount.firstName}'s Service`,
			};

			const response = await callApi({
				method: "POST",
				url: `${apis.CLONE_COURSE}/${id}`,
				body,
			});

			if (!response) {
				return;
			}

			const course: TCourse = {
				...response.course,
				courseDocuments: response.courseDocuments,
				lesson: response.lessons,
			};
			MyServicesService.addCourse(course);
			MyServicesService.emitChanges();
			return course;
		} catch (error) {}
	};

	return { loading, courses, createCourse, cloneCourse };
}
